import React, { FunctionComponent, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { ApplicationState } from "../../../../../store";
import { bindActionCreators, Dispatch } from "redux";
import ProfileHeader from "../ProfileHeader";
import { Col, Form, OverlayTrigger, Row, Table, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckSquare, faInfoCircle, faMinusSquare, faSquare } from "@fortawesome/free-solid-svg-icons";
import {
  getNotificationSettings,
  updateNotificationSettings,
} from "../../../../../store/notifications/actions";
import LoadingIndicator from "../../../onboarding/LoadingIndicator";
import { NotificationSettings } from "../../../../../store/notifications/types";
import { toastr } from "react-redux-toastr";
import { useIntl } from "react-intl";

type PropsFromState = {
  notificationSettings: Array<NotificationSettings>;
  isLoading: {
    getNotificationSettings: boolean;
    updateNotificationSettings: boolean;
  };
  errors: {
    getNotificationSettings?: string;
    updateNotificationSettings?: string;
  };
};

type DispatchProps = {
  getNotificationSettings: () => any;
  updateNotificationSettings: (preferences: {
    [id: number]: {
      email: boolean;
      in_app: boolean;
    };
  }) => any;
};

type Props = PropsFromState & DispatchProps;

const NotificationsTab: FunctionComponent<Props> = ({
  notificationSettings,
  errors,
  isLoading,
  getNotificationSettings,
  updateNotificationSettings,
}) => {
  const intl = useIntl();

  useEffect(() => {
    getNotificationSettings();
  }, []);
  useEffect(() => {
    if (errors.getNotificationSettings) {
      toastr.error("Error", errors.getNotificationSettings);
    }
  }, [isLoading.getNotificationSettings]);

  useEffect(() => {
    if (errors.updateNotificationSettings) {
      toastr.error("Error", errors.updateNotificationSettings);
    }
  }, [isLoading.updateNotificationSettings]);

  const areAllInAppChecked = useMemo(() => 
    notificationSettings.every(n => n.preferences.in_app)
  , [notificationSettings]);
  const areAllEmailChecked = useMemo(() => 
    notificationSettings.every(n => n.preferences.email)
  , [notificationSettings]);

  const isSomeInAppChecked = useMemo(() =>
    notificationSettings.some(n => n.preferences.in_app) && !areAllInAppChecked
  , [notificationSettings, areAllInAppChecked]);

  const isSomeEmailChecked = useMemo(() =>
    notificationSettings.some(n => n.preferences.email) && !areAllEmailChecked
  , [notificationSettings, areAllEmailChecked]);

  const handleInAppPreferenceChange = (settings: NotificationSettings) => {
    updateNotificationSettings({
      [settings.id]: {
        ...settings.preferences,
        in_app: !settings.preferences.in_app,
      },
    });
  };

  const handleEmailPreferenceChange = (settings: NotificationSettings) => {
    updateNotificationSettings({
      [settings.id]: {
        ...settings.preferences,
        email: !settings.preferences.email,
      },
    });
  };

  const handleAllInAppChange = () => {
    const allChecked = areAllInAppChecked;
    const newPrefs: { [id: string]: { email: boolean; in_app: boolean } } = {};
    notificationSettings.forEach(n => {
      newPrefs[n.id] = {
        ...n.preferences,
        in_app: !allChecked,
      };
    });
    updateNotificationSettings(newPrefs);
  };

  const handleAllEmailChange = () => {
    const allChecked = areAllEmailChecked;
    const newPrefs: { [id: string]: { email: boolean; in_app: boolean } } = {};
    notificationSettings.forEach(n => {
      newPrefs[n.id] = {
        ...n.preferences,
        email: !allChecked,
      };
    });
    updateNotificationSettings(newPrefs);
  };


   return (
    <div className="dataContainer">
      <div>
        <h2 className="font-weight-bold mt-2">
          {intl.formatMessage({
            id: "app.dashboard.notifications.title",
          })}
        </h2>
        <h4>
          {intl.formatMessage({
            id: "app.dashboard.notifications.tab.info",
          })}
        </h4>
        <hr />
      </div>
      {isLoading.getNotificationSettings ? (
        <LoadingIndicator />
      ) : (
        <Table responsive>
          <thead>
            <tr
              className="font-weight-bold"
              style={{ fontSize: "18px", backgroundColor: "#78909c", color: "white" }}
            >
              <th>
                <span className="font-weight-bold">
                  {intl.formatMessage({
                    id: "app.dashboard.notifications.tab.whichNotification.label",
                  })}
                </span>
              </th>
              <th className="text-center">
                {intl.formatMessage({
                  id: "app.dashboard.notifications.in-app.title",
                })}
                <OverlayTrigger
                  trigger={["hover", "focus"]}
                  placement="top"
                  overlay={
                    <Tooltip className="customTooltip" id="tooltip-top">
                      In-app notifications appear within the application.
                    </Tooltip>
                  }
                >
                    <span className="ml-2">
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      className="pointer"
                      style={{ color: "#ffffff", fontSize: "20px" }}
                      // Setting the font size to 20px makes the icon appear clearly
                    />
                    </span>
                </OverlayTrigger>
              </th> 
              <th className="text-center">
                {intl.formatMessage({
                  id: "app.forms.email.label",
                }).toUpperCase()}
                
                <OverlayTrigger
                  trigger={["hover", "focus"]}
                  placement="top"
                  overlay={
                    <Tooltip className="customTooltip" id="tooltip-top">
                      Email notifications are sent to your registered email address.
                    </Tooltip>
                  }
                >
                  <span className="ml-2">
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      className="pointer"
                      style={{ color: "#ffffff", fontSize: "20px" }}
                      // Setting the font size to 20px makes the icon appear clearly
                    />
                  </span>
                </OverlayTrigger>
              </th>
            </tr>
          </thead>
          <tbody>
            {/* All notification row */}
            <tr>
              <td className="font-weight-bold">All Notifications</td>
              <td className="text-center">
                <div onClick={handleAllInAppChange}>
                  <OverlayTrigger
                    trigger={["hover", "focus"]}
                    placement="left"
                    overlay={
                      <Tooltip className="customTooltip" id= "tooltip-top">
                        {areAllInAppChecked ? "Turn off all notifications" : "Turn on all notifications"}
                      </Tooltip>
                    }
                  >
                    <span style={{ cursor: "pointer" }}>
                      <FontAwesomeIcon
                        icon={areAllInAppChecked ? faCheckSquare : isSomeInAppChecked ? faMinusSquare : faSquare}
                        style={
                          (areAllInAppChecked || isSomeInAppChecked) ? { color: "#00275b" } : { color: "#b5bdc1" }
                        }
                        className="fontAwesomeIcon pointer"
                        size="lg"
                        />
                    </span>
                  </OverlayTrigger>
                </div>
              </td>
              <td className="text-center">
                <div onClick={handleAllEmailChange}>
                  <OverlayTrigger
                    trigger={["hover", "focus"]}
                    placement="left"
                    overlay={
                      <Tooltip className="customTooltip" id= "tooltip-top">
                        {areAllEmailChecked ? "Turn off all notifications" : "Turn on all notifications"}
                      </Tooltip>
                    }
                  >
                    <span style={{ cursor: "pointer" }}>
                      <FontAwesomeIcon
                        icon={areAllEmailChecked ? faCheckSquare : isSomeEmailChecked ? faMinusSquare : faSquare}
                        style={
                          (areAllEmailChecked || isSomeEmailChecked) ? { color: "#00275b" } : { color: "#b5bdc1" }
                        }
                        className="fontAwesomeIcon pointer"
                        size="lg"
                        />
                    </span>
                  </OverlayTrigger>
                </div>
              </td> 
            </tr>
            {/* Individual notification rows */}
            {notificationSettings.map((notification) => (
              <tr key={notification.id}>
                <td>{notification.label}</td>
                <td className="text-center">
                  <div
                    onClick={() => handleInAppPreferenceChange(notification)}
                  >  
                    <OverlayTrigger
                      trigger={["hover", "focus"]}
                      placement="left"
                      overlay={
                        <Tooltip className="customTooltip" id= {`tooltip-top-${notification.id}`}>
                          {notification.preferences.in_app ? "Turn off" : "Turn on"}
                        </Tooltip>
                      }
                    >
                      <span style={{ cursor: "pointer" }}>
                        <FontAwesomeIcon
                          icon={notification.preferences.in_app ? faCheckSquare : faSquare}
                          style={
                            notification.preferences.in_app
                              ? { color: "#00275b" }
                              : { color: "#b5bdc1" }
                          }
                          className={"fontAwesomeIcon pointer"}
                          size={"lg"}
                        />
                      </span>
                    </OverlayTrigger>
                  </div>
                </td>
                <td className="text-center">
                  <div
                    onClick={() => handleEmailPreferenceChange(notification)}
                  >
                    <OverlayTrigger
                      trigger={["hover", "focus"]}
                      placement="left"
                      overlay={
                        <Tooltip className="customTooltip" id= {`tooltip-top-${notification.id}`}>
                          {notification.preferences.email ? "Turn off" : "Turn on"}
                        </Tooltip>
                      }
                    >
                      <span style={{ cursor: "pointer" }}>
                        <FontAwesomeIcon
                          icon={notification.preferences.email ? faCheckSquare : faSquare}
                          style={
                            notification.preferences.email
                              ? { color: "#00275b" }
                              : { color: "#b5bdc1" }
                          }
                          className={"fontAwesomeIcon pointer"}
                          size={"lg"}
                        />
                      </span>
                    </OverlayTrigger>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        // <>
        //   <Row className="mt-4">
        //     <Col md={8}>
        //       <span className="font-weight-bold">
        //         {intl.formatMessage({
        //           id: "app.dashboard.notifications.tab.whichNotification.label",
        //         })}
        //       </span>
        //     </Col>
        //     <Col md={2} className="notificationsHeader text-center">
        //       {intl.formatMessage({
        //         id: "app.dashboard.notifications.notification.title",
        //       })}
        //     </Col>
        //     <Col md={2} className="notificationsHeader text-center">
        //       {intl.formatMessage({
        //         id: "app.forms.email.label",
        //       })}
        //     </Col>
        //   </Row>
        //   <hr />
        //   <>
        //     {notificationSettings.map((notification) => (
        //       <Row key={notification.id} className="mt-4">
        //         <Col md={8}>{notification.label}</Col>
        //         <Col md={2} className="displayCentered">
        //           <div
        //             onClick={() => handleInAppPreferenceChange(notification)}
        //           >
        //             <FontAwesomeIcon
        //               icon={faBell}
        //               style={
        //                 notification.preferences.in_app
        //                   ? { color: "#e65101" }
        //                   : { color: "#b5bdc1" }
        //               }
        //               className={"fontAwesomeIcon pointer"}
        //               size={"lg"}
        //             />
        //           </div>
        //         </Col>
        //         <Col md={2} className="displayCentered">
        //           <div
        //             onClick={() => handleEmailPreferenceChange(notification)}
        //           >
        //             <FontAwesomeIcon
        //               icon={faEnvelope}
        //               style={
        //                 notification.preferences.email
        //                   ? { color: "#e65101" }
        //                   : { color: "#b5bdc1" }
        //               }
        //               className={"fontAwesomeIcon pointer"}
        //               size={"lg"}
        //             />
        //           </div>
        //         </Col>
        //       </Row>
        //     ))}
        //   </>
        // </>
      )}
    </div>
  );
};

const mapStateToProps = ({
  notifications,
}: ApplicationState): PropsFromState => {
  return {
    notificationSettings: notifications.notificationSettings,
    isLoading: {
      getNotificationSettings: notifications.isLoading.getNotificationSettings,
      updateNotificationSettings:
        notifications.isLoading.updateNotificationSettings,
    },
    errors: {
      getNotificationSettings: notifications.errors.getNotificationSettings,
      updateNotificationSettings:
        notifications.errors.updateNotificationSettings,
    },
  };
};

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps =>
  bindActionCreators(
    {
      getNotificationSettings: getNotificationSettings,
      updateNotificationSettings: updateNotificationSettings,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(NotificationsTab);
